import axios from 'axios';
import { getBackendUrl } from '../config';
import { triggerCookieProblemModal } from '../components/CookieProblemModal';
import { clearHeaderTooLargeModalShown, setHadCookieError } from '../utils/storageCleanup';

/** Error code when Nginx returns "Request Header Or Cookie Too Large" (400/413/431). */
export const ERR_HEADER_OR_COOKIE_TOO_LARGE = 'ERR_HEADER_OR_COOKIE_TOO_LARGE';

const NGINX_HEADER_TOO_LARGE_PATTERN = /request header or cookie too large/i;

function isHeaderOrCookieTooLargeResponse(response) {
  if (!response) return false;
  const status = response.status;
  if (status !== 400 && status !== 413 && status !== 431) return false;
  const data = response.data;
  const body = typeof data === 'string' ? data : (data && String(data)) || '';
  const isHtml = /<\s*html\s*[\s>]/i.test(body) || /<\s*!DOCTYPE\s+/i.test(body);
  return isHtml && NGINX_HEADER_TOO_LARGE_PATTERN.test(body);
}

function resolveBaseURL() {
  const runtime = getBackendUrl() || process.env.REACT_APP_BACKEND_URL;
  return runtime ? String(runtime).replace(/\/$/, '') : undefined;
}

const api = axios.create({});

export const openApi = axios.create({});

function withRuntimeBaseURL(config) {
  const baseURL = resolveBaseURL();
  if (!baseURL) return config;

  // Só força baseURL em URLs relativas ("/foo") para não quebrar URLs absolutas.
  if (typeof config?.url === 'string' && config.url.startsWith('/')) {
    // Evita recriar o objeto config (preserva AxiosHeaders e props internas).
    // eslint-disable-next-line no-param-reassign
    config.baseURL = baseURL;
    return config;
  }

  // Se alguém passou url sem "/", mas sem baseURL, ainda assim aplica.
  if (!config?.baseURL) {
    // eslint-disable-next-line no-param-reassign
    config.baseURL = baseURL;
    return config;
  }

  return config;
}

api.interceptors.request.use(withRuntimeBaseURL);
openApi.interceptors.request.use(withRuntimeBaseURL);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const response = error?.response;
    if (isHeaderOrCookieTooLargeResponse(response)) {
      const err = error;
      err.isHeaderOrCookieTooLarge = true;
      err.code = ERR_HEADER_OR_COOKIE_TOO_LARGE;
      setHadCookieError();
      triggerCookieProblemModal();
    }
    return Promise.reject(error);
  }
);

if (process.env.NODE_ENV === 'development' && typeof window !== 'undefined') {
  window.__pingochatSimulateHeaderTooLarge = () => {
    clearHeaderTooLargeModalShown();
    triggerCookieProblemModal();
  };
}

export default api;
